import type { JSX } from 'react';

import type { LucideIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

import type { DomainStyles } from '../../model/activity-display';

interface ActivityDomainChipProps {
  active: boolean;
  label: string;
  count: number | undefined;
  icon?: LucideIcon;
  onClick: () => void;
  styles?: DomainStyles;
}

const ActivityDomainChip = ({
  active,
  label,
  count,
  icon: Icon,
  onClick,
  styles,
}: ActivityDomainChipProps): JSX.Element => {
  const inactive = !active;
  let baseColors: string;
  if (styles) {
    baseColors = inactive ? styles.chipBg : styles.chipActiveBg;
  } else {
    baseColors = inactive
      ? 'bg-bg-surface-raised text-text-muted hover:bg-bg-surface-alt hover:text-text-default'
      : 'bg-brand-vivid text-text-inverse';
  }

  let badgeColors: string;
  if (styles) {
    badgeColors = inactive ? styles.chipBadgeBg : 'bg-white/25 text-current';
  } else {
    badgeColors = inactive ? 'bg-text-muted/15 text-text-muted' : 'bg-white/25 text-current';
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'inline-flex min-h-9 items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-semibold',
        'transition-colors font-body',
        baseColors
      )}
    >
      {Icon ? <Icon className="size-3.5 shrink-0" aria-hidden /> : null}
      {label}
      <span
        className={cn(
          'inline-flex min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-semibold tabular-nums',
          badgeColors
        )}
      >
        {count === undefined ? '—' : count}
      </span>
    </button>
  );
};

export default ActivityDomainChip;
